import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  DollarSign, TrendingUp, AlertTriangle, Repeat, Loader2, Sparkles, CalendarClock,
} from "lucide-react";

function fmtMoney(cents) {
  if (cents == null) return "$0";
  return "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export default function PaymentHealth() {
  const [data, setData] = useState(null);
  const [renewals, setRenewals] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      http.get("/admin/payment-health"),
      http.get("/admin/upcoming-renewals"),
    ])
      .then(([h, r]) => { setData(h.data); setRenewals(r.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card p-6 flex items-center gap-2 text-navy/60">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading payment health…
      </div>
    );
  }

  const d = data || {};
  const noData = !d.has_data;

  return (
    <section data-testid="payment-health" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow"><Sparkles className="inline w-3 h-3 -mt-0.5 mr-1" /> Payment Health</div>
          <h2 className="mt-2 text-3xl">Billing at a glance</h2>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-navy/45">via Bookipi</span>
      </div>

      {noData ? (
        <div className="card p-6 bg-gold/5 border-gold/20 text-sm text-navy/75" data-testid="payment-health-empty">
          <strong className="text-navy">Waiting for Bookipi to start sending data.</strong>{" "}
          Once you set up <Link to="/" className="text-gold underline">Zaps 7–8</Link> in the Setup Guide,
          invoice events (paid, overdue, recurring) will flow in here automatically.
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-5" data-testid="ph-mrr">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-navy/55">MRR</div>
              <div className="font-serif text-3xl text-navy mt-1">{fmtMoney(d.mrr_cents)}</div>
              <div className="text-[10px] uppercase tracking-widest text-teal mt-1">
                {d.active_subscriptions || 0} active subs
              </div>
            </div>
            <Repeat className="w-7 h-7 text-teal" />
          </div>
        </div>

        <div className="card p-5" data-testid="ph-30d">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-navy/55">Revenue · 30d</div>
              <div className="font-serif text-3xl text-navy mt-1">{fmtMoney(d.revenue_30d_cents)}</div>
              <div className="text-[10px] uppercase tracking-widest text-navy/50 mt-1">
                {d.paid_invoices_30d || 0} invoices paid
              </div>
            </div>
            <TrendingUp className="w-7 h-7 text-gold" />
          </div>
        </div>

        <div className="card p-5" data-testid="ph-overdue">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-navy/55">Overdue</div>
              <div className="font-serif text-3xl text-navy mt-1">{d.overdue_count || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-gold mt-1">
                {fmtMoney(d.overdue_amount_cents)} owed
              </div>
            </div>
            <AlertTriangle className={`w-7 h-7 ${d.overdue_count ? "text-red-500" : "text-navy/30"}`} />
          </div>
        </div>

        <div className="card p-5" data-testid="ph-collected">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-navy/55">Avg / sub</div>
              <div className="font-serif text-3xl text-navy mt-1">
                {d.active_subscriptions
                  ? fmtMoney(Math.round(d.mrr_cents / d.active_subscriptions))
                  : "$0"}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-navy/50 mt-1">per month</div>
            </div>
            <DollarSign className="w-7 h-7 text-navy" />
          </div>
        </div>
      </div>

      {(d.overdue_invoices || []).length > 0 && (
        <div className="card p-6" data-testid="ph-overdue-list">
          <h4 className="text-xl mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" /> Needs attention
          </h4>
          <ul className="divide-y divide-navy/5">
            {d.overdue_invoices.map((inv, i) => (
              <li key={inv.bookipi_invoice_id || i} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="text-navy font-medium">
                    {inv.client_name || inv.client_email || "Unknown client"}
                  </div>
                  <div className="text-xs text-navy/55">
                    {inv.title || inv.service_name || "Invoice"} · {fmtMoney(inv.amount_cents)}
                  </div>
                </div>
                {inv.url && (
                  <a href={inv.url} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline">
                    Open in Bookipi
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {renewals && renewals.count > 0 && (
        <div className="card p-6" data-testid="ph-renewals-list">
          <h4 className="text-xl mb-4 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-teal" /> Upcoming renewals ({renewals.count})
          </h4>
          <ul className="divide-y divide-navy/5">
            {renewals.subscriptions.slice(0, 10).map((sub, i) => (
              <li key={sub.bookipi_invoice_id || i} className="py-3 flex items-center justify-between text-sm" data-testid={`renewal-${i}`}>
                <div className="flex items-center gap-3">
                  {sub.client_picture ? (
                    <img src={sub.client_picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-teal/15 text-teal flex items-center justify-center text-xs font-medium">
                      {(sub.client_name || "?")[0]}
                    </div>
                  )}
                  <div>
                    <div className="text-navy font-medium">
                      {sub.client_name || sub.client_email || "Unknown client"}
                    </div>
                    <div className="text-xs text-navy/55">{sub.service_name || "Subscription"}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-navy font-medium">{fmtMoney(sub.amount_cents)}<span className="text-xs text-navy/55 font-normal">/mo</span></div>
                  <div className="text-[10px] uppercase tracking-widest text-teal">{sub.last_status}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
